<template>
  <div v-if="isVisible" class="break-overlay">
    <div class="break-box">
      <div class="break-icon">
        <span class="icon material-symbols-outlined">schedule</span>
      </div>
      <h2 class="break-title">{{ $t("l_System_on_break") }}</h2>
      <p class="break-reason">{{ breakData.reason }}</p>
      <p v-if="breakData.toTime" class="break-time">
        {{ $t("l_Break_ends_at") }}: <b>{{ formatTime(breakData.toTime) }}</b>
      </p>
      <div class="break-actions">
        <a-button type="primary" :loading="refreshLoading" @click="onRefresh">
          <span v-if="!refreshLoading" class="material-symbols-outlined btn-icon">refresh</span>
          {{ $t("l_Refresh") }}
        </a-button>
        <a-button @click="onSignOut">
          <span class="material-symbols-outlined btn-icon">logout</span>
          Выйти из системы
        </a-button>
      </div>
      <div v-if="isAdmin" class="admin-actions">
        <a-popconfirm
          :title="$t('l_Stop_break_confirm')"
          :ok-text="$t('l_Yes')"
          :cancel-text="$t('l_No')"
          placement="bottom"
          :overlayStyle="{ zIndex: 9999999 }"
          @confirm="onStopBreak"
        >
          <a-button type="primary" danger :loading="stopLoading">
            {{ $t("l_Stop_break") }}
          </a-button>
        </a-popconfirm>
      </div>
    </div>
  </div>
</template>

<script>
import { getActiveBreak, stopBreak } from "@/api/systemBreak.js";
import { mapGetters, mapMutations } from "vuex";
import { message } from "ant-design-vue";
import dayjs from "dayjs";

export default {
  data() {
    return {
      stopLoading: false,
      refreshLoading: false,
      expireTimer: null,
    };
  },
  computed: {
    ...mapGetters({
      breakData: "activeSystemBreak",
      isAdmin: "isAdmin",
      isSuperAdmin: "isSuperAdmin",
    }),
    isVisible() {
      if (this.isAdmin || this.isSuperAdmin) return false;
      return Boolean(this.breakData);
    },
  },
  watch: {
    breakData(val) {
      this.clearExpireTimer();
      if (val?.toTime) {
        this.scheduleAutoHide(val.toTime);
      }
    },
  },
  mounted() {
    if (this.breakData?.toTime) {
      this.scheduleAutoHide(this.breakData.toTime);
    }
  },
  beforeUnmount() {
    this.clearExpireTimer();
  },
  methods: {
    ...mapMutations(["signOut"]),
    onSignOut() {
      this.clearExpireTimer();
      this.$store.commit("setSystemBreak", null);
      this.signOut();
    },
    async onRefresh() {
      this.refreshLoading = true;
      try {
        const res = await getActiveBreak();
        if (res?.data) {
          this.$store.commit("setSystemBreak", res.data);
        } else {
          this.$store.commit("setSystemBreak", null);
        }
      } finally {
        this.refreshLoading = false;
      }
    },
    formatTime(t) {
      return t ? dayjs(t).format("DD.MM.YYYY HH:mm") : "";
    },
    scheduleAutoHide(toTime) {
      const diff = dayjs(toTime).diff(dayjs());
      if (diff <= 0) {
        this.$store.commit("setSystemBreak", null);
        return;
      }
      this.expireTimer = setTimeout(() => {
        this.$store.commit("setSystemBreak", null);
      }, diff);
    },
    clearExpireTimer() {
      if (this.expireTimer) {
        clearTimeout(this.expireTimer);
        this.expireTimer = null;
      }
    },
    async onStopBreak() {
      this.stopLoading = true;
      try {
        await stopBreak();
        this.$store.commit("setSystemBreak", null);
        message.success(this.$t("l_Succeed"));
      } catch (e) {
        const msg = e?.response?.data?.message;
        if (msg) message.error(msg);
      } finally {
        this.stopLoading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.break-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999998;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.break-box {
  background: #fff;
  border-radius: 12px;
  padding: 48px 56px;
  text-align: center;
  max-width: 480px;
  width: 90%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);

  .break-icon {
    margin-bottom: 16px;

    .icon {
      font-size: 64px;
      color: #fa8c16;
    }
  }

  .break-title {
    font-size: 24px;
    font-weight: 700;
    color: #262626;
    margin-bottom: 12px;
  }

  .break-reason {
    font-size: 16px;
    color: #595959;
    margin-bottom: 8px;
  }

  .break-time {
    font-size: 14px;
    color: #8c8c8c;
    margin-bottom: 24px;
  }

  .break-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
    margin-top: 8px;
    flex-wrap: wrap;

    .btn-icon {
      font-size: 16px;
      vertical-align: middle;
      margin-right: 4px;
    }
  }

  .admin-actions {
    margin-top: 12px;
  }
}
</style>
